import React from 'react';
import { useTranslation } from 'react-i18next';
import KulkarniConsultingNote from './KulkarniConsultingNote';
import { APP_LOGO_PNG } from '../branding';

/**
 * @param {{ variant?: 'public' | 'postSignup', onBack?: () => void }} props
 */
export default function PricingPage({ variant = 'public', onBack }) {
  const { i18n } = useTranslation();
  const isCzech = (i18n.language || '').toLowerCase().startsWith('cs');
  const isPostSignup = variant === 'postSignup';

  const labels = isCzech
    ? {
        pageTitle: 'Ceník CUZK Automation',
        subtitle: 'Návrh cenového modelu (bez DPH)',
        backToLogin: 'Zpět na přihlášení',
        plansTitle: 'Předplatné',
        servicesTitle: 'Služby šablon (mimo předplatné)',
        billingTitle: 'Fakturační poznámky',
        tableFeature: 'Funkce',
        tableService: 'Služba',
        tablePrice: 'Cena',
        starter: 'Starter',
        growth: 'Growth',
        pro: 'Pro',
        enterprise: 'Enterprise',
        yes: 'Ano',
        no: 'Ne',
        same: 'Stejné',
        custom: 'Vlastní',
        negotiated: 'Dohodou',
        upTo3: 'Až 3',
        upTo5: 'Až 5',
        threeIncluded: '3 v ceně',
        threePlusCustom: '3+ / Vlastní',
        postSignupTitle: 'Vyberte si předplatné',
        postSignupMessage:
          'Účet je vytvořen. Než nastavíte profil a začnete používat aplikaci, je potřeba aktivní placený plán. Platební brána bude brzy k dispozici — zatím nás kontaktujte na info@kulkarni.cz a plán vám aktivujeme ručně.',
        contactCta: 'Napsat na info@kulkarni.cz',
        refreshAccess: 'Už mám aktivovaný plán — obnovit',
        notes: [
          'Poplatek za dokument se účtuje pouze při úspěšném vygenerování výstupu.',
          'Poplatek ČÚZK (100 CZK za pull) je veden jako samostatná průchozí položka a není součástí platformové ceny.',
          '„3 standardní šablony v ceně“ znamená použití knihovny beze změn, nikoliv vlastní úpravu šablon.',
        ],
      }
    : {
        pageTitle: 'CUZK Automation Pricing',
        subtitle: 'Draft pricing matrix (excluding VAT)',
        backToLogin: 'Back to login',
        plansTitle: 'Subscription plans',
        servicesTitle: 'Template services (separate from subscription)',
        billingTitle: 'Billing notes',
        tableFeature: 'Feature',
        tableService: 'Service',
        tablePrice: 'Price',
        starter: 'Starter',
        growth: 'Growth',
        pro: 'Pro',
        enterprise: 'Enterprise',
        yes: 'Yes',
        no: 'No',
        same: 'Same',
        custom: 'Custom',
        negotiated: 'Negotiated',
        upTo3: 'Up to 3',
        upTo5: 'Up to 5',
        threeIncluded: '3 included',
        threePlusCustom: '3+ / Custom',
        postSignupTitle: 'Choose a paid plan',
        postSignupMessage:
          'Your account is ready. Profile setup stays locked until a paid plan is active. Online checkout is coming soon — for now email info@kulkarni.cz and we will activate your plan manually.',
        contactCta: 'Email info@kulkarni.cz',
        refreshAccess: 'My plan was activated — refresh',
        notes: [
          'Document charge applies on successful generated output.',
          'CUZK fee (100 CZK pull) is billed as a separate pass-through line item and is not mixed with platform pricing.',
          '“3 standard templates included” means library use as-is and does not include custom adaptation.',
        ],
      };

  const pricingRows = [
    [isCzech ? 'Měsíční poplatek' : 'Monthly fee', '590 CZK', '1,790 CZK', '4,290 CZK', labels.custom],
    [isCzech ? 'Dokumenty v ceně / měsíc' : 'Included docs / month', '10', '60', '200', labels.custom],
    [isCzech ? 'Nadlimit / extra dokument' : 'Overage / extra doc', '39 CZK', '24 CZK', '15 CZK', labels.negotiated],
    [isCzech ? 'Uživatelé v ceně' : 'Included users', '1', labels.upTo3, labels.upTo5, labels.custom],
    [
      isCzech ? 'Standardní šablony (bez úprav)' : 'Standard templates (use as-is)',
      labels.threeIncluded,
      labels.threeIncluded,
      labels.threeIncluded,
      labels.threePlusCustom,
    ],
    [
      isCzech ? 'Standardní šablony v ceně' : 'Included standard templates',
      isCzech ? 'Objednávka pronájmu (byt), zprostředkování (byt), GDPR' : 'Rent order (apartment), Sale order (apartment), GDPR',
      labels.same,
      labels.same,
      isCzech ? 'Stejné + vlastní katalog' : 'Same + custom catalog',
    ],
    [isCzech ? 'Stav šablon + varování před přepsáním' : 'Template status + overwrite warning', labels.yes, labels.yes, labels.yes, labels.yes],
    [isCzech ? 'Admin funkce' : 'Admin controls', labels.no, labels.yes, labels.yes, labels.yes],
    [isCzech ? 'Měřič využití (kvóta + nadlimit)' : 'Usage meter (quota + overage)', labels.yes, labels.yes, labels.yes, labels.yes],
    [isCzech ? 'Prioritní podpora' : 'Priority support', labels.no, labels.yes, labels.yes, labels.yes],
    [isCzech ? 'Cílová SLA odezva' : 'SLA target response', labels.no, labels.no, labels.yes, labels.yes],
    [isCzech ? 'Pokročilý reporting' : 'Advanced reporting', labels.no, labels.no, labels.yes, labels.yes],
    [isCzech ? 'Dedikovaný onboarding' : 'Dedicated onboarding', labels.no, labels.no, labels.no, labels.yes],
  ];

  const serviceRows = [
    [isCzech ? 'Úprava šablony (menší změny)' : 'Template customization (minor edits)', '2,900 CZK / template (one-time)'],
    [isCzech ? 'Vývoj nové šablony na míru' : 'Bespoke custom template build', 'from 12,000 CZK / template (one-time)'],
    [isCzech ? 'Retainer na údržbu šablon (volitelné)' : 'Template maintenance retainer (optional)', '1,490 CZK / month'],
    [isCzech ? 'Ad-hoc práce na šablonách (volitelné)' : 'Ad-hoc template work (optional)', '1,200 CZK / hour'],
  ];

  return (
    <div className={`app-shell-bg ${isPostSignup ? '' : 'min-h-[100dvh]'} flex flex-col px-3 sm:px-4 w-full max-w-full min-w-0`}>
      <div className="flex-1 py-6 sm:py-8">
        <div className="w-full max-w-6xl mx-auto rounded-2xl border border-emerald-200/80 bg-white/95 shadow-card backdrop-blur-sm p-5 sm:p-8 lg:p-10">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
            <div className="flex items-center gap-4">
              <img
                src={APP_LOGO_PNG}
                alt=""
                className="h-16 sm:h-20 w-auto object-contain"
                decoding="async"
              />
              <div>
                <h1 className="text-2xl sm:text-3xl font-sans font-extrabold text-brand-green tracking-tight">
                  {isPostSignup ? labels.postSignupTitle : labels.pageTitle}
                </h1>
                <p className="text-slate-600 text-sm sm:text-base font-medium mt-1">
                  {isPostSignup ? labels.postSignupMessage : labels.subtitle}
                </p>
              </div>
            </div>
            {!isPostSignup && (
              onBack ? (
                <button
                  type="button"
                  onClick={onBack}
                  className="inline-flex items-center justify-center rounded-xl border border-emerald-300 px-4 py-2.5 text-sm font-semibold text-brand-green hover:bg-emerald-50"
                >
                  {labels.backToLogin}
                </button>
              ) : (
                <a
                  href="/"
                  className="inline-flex items-center justify-center rounded-xl border border-emerald-300 px-4 py-2.5 text-sm font-semibold text-brand-green hover:bg-emerald-50"
                >
                  {labels.backToLogin}
                </a>
              )
            )}
          </div>

          {isPostSignup && (
            <div className="mb-8 flex flex-col sm:flex-row gap-3">
              <a
                href="mailto:info@kulkarni.cz?subject=CUZK%20Automation%20plan%20activation"
                className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-brand-emerald to-emerald-700 px-5 py-3 text-sm font-bold text-white shadow-soft hover:from-emerald-700 hover:to-brand-emerald"
              >
                {labels.contactCta}
              </a>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="inline-flex items-center justify-center rounded-xl border border-emerald-300 px-5 py-3 text-sm font-semibold text-brand-green hover:bg-emerald-50"
              >
                {labels.refreshAccess}
              </button>
            </div>
          )}

          <section className="mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-brand-green mb-4">{labels.plansTitle}</h2>
            <div className="overflow-x-auto rounded-xl border border-emerald-100">
              <table className="w-full min-w-[880px] text-sm">
                <thead className="bg-emerald-50/60">
                  <tr>
                    <th className="px-4 py-3 text-left font-bold text-slate-800">{labels.tableFeature}</th>
                    <th className="px-4 py-3 text-left font-bold text-slate-800">{labels.starter}</th>
                    <th className="px-4 py-3 text-left font-bold text-slate-800">{labels.growth}</th>
                    <th className="px-4 py-3 text-left font-bold text-slate-800">{labels.pro}</th>
                    <th className="px-4 py-3 text-left font-bold text-slate-800">{labels.enterprise}</th>
                  </tr>
                </thead>
                <tbody>
                  {pricingRows.map((row) => (
                    <tr key={row[0]} className="border-t border-emerald-100/80">
                      <td className="px-4 py-3 font-semibold text-slate-800">{row[0]}</td>
                      <td className="px-4 py-3 text-slate-700">{row[1]}</td>
                      <td className="px-4 py-3 text-slate-700">{row[2]}</td>
                      <td className="px-4 py-3 text-slate-700">{row[3]}</td>
                      <td className="px-4 py-3 text-slate-700">{row[4]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-brand-green mb-4">
              {labels.servicesTitle}
            </h2>
            <div className="overflow-x-auto rounded-xl border border-emerald-100">
              <table className="w-full min-w-[620px] text-sm">
                <thead className="bg-emerald-50/60">
                  <tr>
                    <th className="px-4 py-3 text-left font-bold text-slate-800">{labels.tableService}</th>
                    <th className="px-4 py-3 text-left font-bold text-slate-800">{labels.tablePrice}</th>
                  </tr>
                </thead>
                <tbody>
                  {serviceRows.map((row) => (
                    <tr key={row[0]} className="border-t border-emerald-100/80">
                      <td className="px-4 py-3 font-semibold text-slate-800">{row[0]}</td>
                      <td className="px-4 py-3 text-slate-700">{row[1]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-brand-green mb-3">{labels.billingTitle}</h2>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 text-sm sm:text-base">
              {labels.notes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          </section>
        </div>
      </div>
      <KulkarniConsultingNote className="mx-[-0.75rem] sm:mx-[-1rem] mt-4" />
    </div>
  );
}
